// CSP-safe JSON Schema subset validator.
//
// Replaces Ajv at runtime. Ajv compiles validators via `new Function(...)`,
// which violates Chrome MV3's `script-src 'self' 'wasm-unsafe-eval'` CSP
// (no `'unsafe-eval'`, no remote script). This validator never calls
// `new Function` or `eval`; it walks the schema tree directly.
//
// Supported subset (Draft-07) — chosen to exactly cover the schemas used by
// `src/lib/llm/prompts/*`:
//   - type: 'object' | 'string' | 'number' | 'integer' | 'array' | 'boolean' | 'null'
//   - required: string[]
//   - properties: Record<string, Schema>
//   - additionalProperties: false   (strict mode)
//   - enum: unknown[]
//   - const: unknown
//   - minimum, maximum               (number)
//   - minLength, maxLength           (string)
//   - pattern                        (string — compiled with RegExp, not eval)
//   - items: Schema                  (single sub-schema)
//   - minItems, maxItems             (array)
//
// Not supported (intentionally): $ref, $defs/definitions, oneOf/anyOf/allOf,
// if/then/else, dependentRequired, prefixItems, tuple items, format keyword.
// The EQUATOR _schema.json uses $ref and oneOf; that schema is enforced by
// `scripts/checklists-ci.mjs` at build time and not re-validated at runtime.

export interface ValidationError {
  instancePath: string;
  message: string;
}

export interface ValidateFn {
  (data: unknown): boolean;
  errors: ValidationError[] | null;
}

/** Compile a schema into a CSP-safe validator function. */
export function compile(schema: unknown): ValidateFn {
  const fn = ((data: unknown) => {
    const errs: ValidationError[] = [];
    walk(data, schema, '', errs);
    fn.errors = errs.length ? errs : null;
    return errs.length === 0;
  }) as ValidateFn;
  fn.errors = null;
  return fn;
}

function walk(
  data: unknown,
  schema: unknown,
  path: string,
  errs: ValidationError[],
): void {
  if (typeof schema !== 'object' || schema === null) return;
  const s = schema as Record<string, unknown>;

  // const / enum first — they short-circuit
  if ('const' in s && !deepEqual(data, s.const)) {
    errs.push({ instancePath: path, message: 'must be equal to constant' });
    return;
  }
  if (Array.isArray(s.enum)) {
    if (!s.enum.some((v) => deepEqual(v, data))) {
      errs.push({ instancePath: path, message: 'must be equal to one of the allowed values' });
      return;
    }
  }

  if (typeof s.type === 'string' && !checkType(data, s.type)) {
    errs.push({ instancePath: path, message: `must be ${s.type}` });
    return;
  }

  if (typeof data === 'number') {
    if (typeof s.minimum === 'number' && data < s.minimum) {
      errs.push({ instancePath: path, message: `must be >= ${s.minimum}` });
    }
    if (typeof s.maximum === 'number' && data > s.maximum) {
      errs.push({ instancePath: path, message: `must be <= ${s.maximum}` });
    }
  }

  if (typeof data === 'string') {
    if (typeof s.minLength === 'number' && data.length < s.minLength) {
      errs.push({
        instancePath: path,
        message: `must NOT have fewer than ${s.minLength} characters`,
      });
    }
    if (typeof s.maxLength === 'number' && data.length > s.maxLength) {
      errs.push({
        instancePath: path,
        message: `must NOT have more than ${s.maxLength} characters`,
      });
    }
    if (typeof s.pattern === 'string') {
      try {
        if (!new RegExp(s.pattern).test(data)) {
          errs.push({ instancePath: path, message: `must match pattern "${s.pattern}"` });
        }
      } catch {
        // Malformed schema-side pattern — silently skip; build-time CI catches these.
      }
    }
  }

  if (Array.isArray(data)) {
    if (typeof s.minItems === 'number' && data.length < s.minItems) {
      errs.push({
        instancePath: path,
        message: `must NOT have fewer than ${s.minItems} items`,
      });
    }
    if (typeof s.maxItems === 'number' && data.length > s.maxItems) {
      errs.push({
        instancePath: path,
        message: `must NOT have more than ${s.maxItems} items`,
      });
    }
    if (s.items && typeof s.items === 'object') {
      data.forEach((el, i) => walk(el, s.items, `${path}/${i}`, errs));
    }
  }

  if (data !== null && typeof data === 'object' && !Array.isArray(data)) {
    const obj = data as Record<string, unknown>;
    const props =
      s.properties && typeof s.properties === 'object'
        ? (s.properties as Record<string, unknown>)
        : {};
    if (Array.isArray(s.required)) {
      for (const key of s.required as string[]) {
        if (!(key in obj)) {
          errs.push({
            instancePath: path,
            message: `must have required property '${key}'`,
          });
        }
      }
    }
    if (s.additionalProperties === false) {
      for (const key of Object.keys(obj)) {
        if (!(key in props)) {
          errs.push({
            instancePath: path,
            message: `must NOT have additional property '${key}'`,
          });
        }
      }
    }
    for (const [key, sub] of Object.entries(props)) {
      if (key in obj) walk(obj[key], sub, `${path}/${key}`, errs);
    }
  }
}

function checkType(data: unknown, type: string): boolean {
  switch (type) {
    case 'object':
      return data !== null && typeof data === 'object' && !Array.isArray(data);
    case 'array':
      return Array.isArray(data);
    case 'string':
      return typeof data === 'string';
    case 'number':
      return typeof data === 'number' && Number.isFinite(data);
    case 'integer':
      return typeof data === 'number' && Number.isInteger(data);
    case 'boolean':
      return typeof data === 'boolean';
    case 'null':
      return data === null;
    default:
      return true;
  }
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a === null || b === null) return false;
  if (typeof a !== 'object') return false;
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    return a.every((v, i) => deepEqual(v, b[i]));
  }
  if (Array.isArray(b)) return false;
  const ak = Object.keys(a as object);
  const bk = Object.keys(b as object);
  if (ak.length !== bk.length) return false;
  return ak.every((k) =>
    deepEqual(
      (a as Record<string, unknown>)[k],
      (b as Record<string, unknown>)[k],
    ),
  );
}
